import React, { useState, useEffect } from 'react';
import { Tooltip } from 'antd';
import moment from 'moment';
import { fetchApiAuth } from '../../core/utils/api';
import constants from '../../core/constants';
import { DeviceTimelineSegment, DeviceTimelineStatus, GeofenceTimelineSegment } from '../../types/types';

import './deviceTimeline.scss';

export const getDeviceTimelineStatusText = (status: DeviceTimelineStatus) => {
    switch (status) {
        case DeviceTimelineStatus.Idle:
            return 'Idle';
        case DeviceTimelineStatus.Moving:
            return 'Moving';
        case DeviceTimelineStatus.Stopped:
            return 'Stopped';
        default:
            return 'Stopped';
    }
}

interface DeviceTimelineProps {
    timelineData?: DeviceTimelineSegment[];
    geofenceData?: GeofenceTimelineSegment[];
    deviceDetails?: {
        date: string;
        assetId?: string;
        deviceId?: string;
    };
    logicalWidth: number;
    scaleX: number;
    isInline?: boolean;
    style?: React.CSSProperties;
    onFetchedData?: (data: any) => void;
}

const DeviceTimeline: React.FC<DeviceTimelineProps> = ({
    timelineData = [], 
    geofenceData = [],
    deviceDetails = {
        date: '',
        assetId: '',
        deviceId: '',
    },
    logicalWidth = 1, 
    scaleX = 1, 
    isInline = false, 
    style = {},
    onFetchedData = () => {},
}) => {
    const [ isFetching, setIsFetching ] = useState<boolean>(false);
    const [ fetchedTimelineData, setFetchedTimelineData ] = useState<DeviceTimelineSegment[]>([]);
    const [ fetchedGeofenceData, setFetchedGeofenceData ] = useState<GeofenceTimelineSegment[]>([]);
    const [ hoveredJourneyId, setHoveredJourneyId ] = useState<string | null>(null);
    const [ timelineDataAbortController, setTimelineDataAbortController ] = useState<AbortController | null>(null);
    const [ geofenceDataAbortController, setGeofenceDataAbortController ] = useState<AbortController | null>(null);
    let actualTimelineData = timelineData;
    let geofenceTimelineData = geofenceData;
    if (!actualTimelineData || !actualTimelineData?.length) {
        actualTimelineData = fetchedTimelineData || [];
    }
    if (!geofenceTimelineData || !geofenceTimelineData?.length) {
        geofenceTimelineData = fetchedGeofenceData || [];
    }
    const timelineComponent = (
        <div
            className={`device-timeline ${hoveredJourneyId ? 'focused-journey' : ''}`}
            style={{
                width: logicalWidth * scaleX,
                height: isInline ? '26px' : '32px',
                position: isInline ? 'relative' : 'absolute',
                top: isInline ? '0px' : '2px',
                left: isInline ? '0px' : '6px',
                marginTop: isInline ? '-4px' : '0px',
                marginBottom: isInline ? '-4px' : '0px',
                pointerEvents: isInline ? 'all' : 'none',
                ...style,
            }}
        >
            {actualTimelineData ? actualTimelineData.map((block, i) => {
                const onClick = () => {
                    if (block.journey_id) {
                        window.open(`/journey/${block.journey_id}`, '_blank')?.focus();
                    }
                };

                let journeyStart = null;
                let journeyEnd = null;
                const safeStatus = block.status ?? DeviceTimelineStatus.Stopped;
                const statusText = getDeviceTimelineStatusText(safeStatus);
                const blockTimeRange = `${moment(block.start_time).format(constants.TIMELINE_DATE_FORMAT)} - ${moment(block.end_time).format(constants.TIMELINE_DATE_FORMAT)}`;
                const blockLocation = block.location;

                if (block.journey_start && block.journey_end) {
                    journeyStart = block.journey_start ? moment(block.journey_start).format(constants.TIMELINE_DATE_FORMAT) : null;
                    journeyEnd = block.journey_end ? moment(block.journey_end).format(constants.TIMELINE_DATE_FORMAT) : null;
                }

                return (
                    <Tooltip
                        key={`timeline-block-tooltip-${i}-${block.start_time}`}
                        title={(
                            <div>
                                {block.journey_id ? (
                                    <>
                                        <div style={{ textAlign: 'center' }}>{block.journey_name}</div>
                                        <div style={{
                                            textAlign: 'center',
                                            margin: '0px'
                                        }}>
                                            <span>{journeyStart} - {journeyEnd}</span>
                                        </div>
                                    </>
                                ) : null}

                                <div style={{ textAlign: 'center' }}>{statusText}</div>
                                <div style={{
                                    textAlign: 'center',
                                    margin: '0px'
                                }}>
                                    {blockTimeRange}
                                </div>
                                {blockLocation ? (
                                    <div style={{ textAlign: 'center' }}>{blockLocation}</div>
                                ) : null}
                            </div>
                        )}>
                        <div
                            key={`timeline-block-${i}-${block.start_time}`}
                            className={`block-${safeStatus} ${isInline ? ' inline' : ''} ${!block.journey_id ? 'not-clickable' : ''} ${hoveredJourneyId && hoveredJourneyId === String(block.journey_id) ? 'is-focused' : ''}`}
                            onClick={onClick}
                            onMouseEnter={() => { 
                                if (block.journey_id) {
                                    setHoveredJourneyId(String(block.journey_id));
                                } else {
                                    setHoveredJourneyId(null);
                                }
                            }}
                            onMouseLeave={() => setHoveredJourneyId(null)}
                            style={{
                                width: (block.end - block.start) * scaleX,
                                left: `${block.start * scaleX}px`,
                            }}
                        />
                    </Tooltip>
                );
            }) : null}
            {geofenceTimelineData ? geofenceTimelineData.map((block, i) => {
                const blockTimeRange = `${moment(block.start_time).format('hh:mm:ss A')} - ${moment(block.end_time).format('hh:mm:ss A')}`;
                return (
                    <Tooltip
                        key={`geofence-timeline-block-tooltip-${i}-${block.start_time}`}
                        title={(
                            <div>
                                <div style={{ textAlign: 'center' }}>
                                    In Geo-fence{block.geofence_names.length > 1 ? 's' : ''}:
                                    <br />
                                    {block.geofence_names.join(', ')}
                                </div>
                                <span>{blockTimeRange}</span>
                            </div>
                        )}
                    >
                        <div
                            key={`geofence-timeline-block-${i}`}
                            className={`geofence-block${isInline ? ' inline' : ''}`}
                            style={{
                                width: (block.end - block.start) * scaleX,
                                left: `${block.start * scaleX}px`,
                            }}
                        />
                    </Tooltip>
                );
            }) : null}
        </div>
    );

    const fetchData = () => {
        if (!deviceDetails || !deviceDetails.date || (!deviceDetails.assetId && !deviceDetails.deviceId)) {
            return;
        }
        if (isFetching) {
            return;
        }
        setIsFetching(true);
        const dateWithoutTime = deviceDetails.date.split(' ')[0];
        const body: { date: string; asset_id?: string; device_id?: string } = {
            date: dateWithoutTime,
        };
        if (deviceDetails.assetId) {
            body.asset_id = deviceDetails.assetId;
        } else {
            body.device_id = deviceDetails.deviceId;
        }
        const timelineDataAbortController = new AbortController();
        const geofenceDataAbortController = new AbortController();
        const promises = [];
        promises.push(new Promise((resolve, reject) => {
            fetchApiAuth({
                method: 'POST',
                url: '/device/timeline',
                body,
                signal: timelineDataAbortController.signal,
            })
                .then((response) => {
                    setFetchedTimelineData(response.data);
                    onFetchedData(response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        }));
        promises.push(new Promise((resolve, reject) => {
            fetchApiAuth({
                method: 'POST',
                url: '/device/geofence-timeline',
                body,
                signal: geofenceDataAbortController.signal,
            })
                .then((response) => {
                    setFetchedGeofenceData(response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        }));
        setTimelineDataAbortController(timelineDataAbortController);
        setGeofenceDataAbortController(geofenceDataAbortController);
        Promise.all(promises)
            .then(() => {
                setTimelineDataAbortController(null);
                setGeofenceDataAbortController(null);
                setIsFetching(false);
            })
            .catch(() => {
                setTimelineDataAbortController(null);
                setGeofenceDataAbortController(null);
            });
    };

    useEffect(() => {
        if (
            (!timelineData && !geofenceData) || 
            ((timelineData && !timelineData.length) && (geofenceData && !geofenceData.length))
        ) {
            fetchData();
        }

        return () => {
            if (timelineDataAbortController) timelineDataAbortController.abort();
            if (geofenceDataAbortController) geofenceDataAbortController.abort();
        };
    }, []);
    useEffect(() => {
        fetchData();
    }, [deviceDetails.date])

    return timelineComponent;
};

export default DeviceTimeline;
